<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Welcome to the MyUser Demo!</h2>
          <p>Enter your contact info to see personalized demo messages</p>
        </div>

        <form @submit.prevent="handleSubmit" class="demo-form">
          <div class="form-group">
            <label for="email">Email Address</label>
            <input
              id="email"
              v-model="email"
              type="email"
              placeholder="you@example.com"
              required
            />
          </div>

          <div class="form-group">
            <label for="phone">Phone Number</label>
            <input
              id="phone"
              v-model="phone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              required
            />
          </div>

          <div class="consent-group">
            <label class="checkbox-label">
              <input v-model="consentEmail" type="checkbox" required />
              <span>I agree to receive emails</span>
            </label>
            <label class="checkbox-label">
              <input v-model="consentSms" type="checkbox" required />
              <span>I agree to receive SMS messages</span>
            </label>
          </div>

          <div class="modal-footer">
            <button
              type="submit"
              class="btn-primary"
              :disabled="loading"
              @touchend.prevent="handleSubmit"
            >
              {{ loading ? 'Saving...' : 'Start Demo' }}
            </button>
            <button
              type="button"
              class="btn-secondary"
              @click="handleSkip"
              @touchend.prevent="handleSkip"
            >
              Skip for now
            </button>
          </div>
        </form>

        <p class="disclaimer">
          This is a demo environment. No real messages will be sent.
        </p>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, watch, toRef } from 'vue';
import { useDemoStore } from '@/store/demoStore';
import axios from 'axios';
import { hapticModal } from '@/utils/haptics';

import { API_BASE } from '@/api/apiBase';
const API_BASE_URL = `${API_BASE}/v2`;

export default defineComponent({
  name: 'DemoOnboardingModal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    projectID: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const demoStore = useDemoStore();

    // Haptic when modal opens
    watch(toRef(props, 'show'), (val) => {
      if (val) hapticModal();
    });

    const email = ref('');
    const phone = ref('');
    const consentEmail = ref(false);
    const consentSms = ref(false);
    const loading = ref(false);

    const handleSubmit = async () => {
      loading.value = true;
      try {
        await axios.post(
          `${API_BASE_URL}/project/${props.projectID}/demo/set-contact`,
          {
            email: email.value,
            phone: phone.value
          }
        );

        // Also save to the waitlist collection (fire-and-forget)
        axios
          .post(`${API_BASE_URL}/waitlist`, {
            email: email.value,
            phone: phone.value,
            source: 'demo-onboarding'
          })
          .catch(() => {
            /* non-critical */
          });

        demoStore.setContact({
          email: email.value,
          phone: phone.value
        });

        emit('close');
      } catch (error) {
        console.error('Failed to save demo contact:', error);
        alert('Failed to save contact info. Please try again.');
      } finally {
        loading.value = false;
      }
    };

    const handleSkip = () => {
      emit('close');
    };

    return {
      email,
      phone,
      consentEmail,
      consentSms,
      loading,
      handleSubmit,
      handleSkip
    };
  }
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-header {
  margin-bottom: 24px;
}

.modal-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
}

.modal-header p {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.demo-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.form-group input {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.consent-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 6px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
}

.checkbox-label input[type='checkbox'] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.modal-footer {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  flex: 1;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.disclaimer {
  margin-top: 16px;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
}

/* Mobile responsive */
@media screen and (max-width: 768px) {
  .modal-overlay {
    align-items: center;
    justify-content: center;
    padding: 12px;
    padding-top: 5vh;
    overflow: hidden;
  }

  .modal-content {
    width: 100%;
    max-width: 100%;
    border-radius: 20px;
    padding: 20px 18px;
    max-height: calc(100dvh - 48px);
    overflow-y: visible;
    overflow-x: hidden;
  }

  .btn-primary,
  .btn-secondary {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    position: relative;
    z-index: 10;
  }

  .modal-header {
    margin-bottom: 12px;
  }

  .modal-header h2 {
    font-size: 18px;
  }

  .modal-header p {
    font-size: 12px;
  }

  .demo-form {
    gap: 10px;
  }

  .form-group {
    gap: 4px;
  }

  .form-group label {
    font-size: 13px;
  }

  .form-group input {
    padding: 9px 12px;
    font-size: 16px; /* prevents iOS zoom on focus */
    border-radius: 10px;
  }

  .consent-group {
    padding: 10px 12px;
    border-radius: 10px;
    gap: 8px;
  }

  .checkbox-label {
    font-size: 13px;
    gap: 8px;
  }

  .checkbox-label input[type='checkbox'] {
    width: 18px;
    height: 18px;
  }

  .modal-footer {
    flex-direction: column;
    gap: 6px;
    margin-top: 2px;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    padding: 11px 20px;
    font-size: 15px;
    border-radius: 10px;
  }

  .disclaimer {
    margin-top: 8px;
    margin-bottom: 0;
    font-size: 11px;
  }
}
</style>
